import { useState } from "react";
import { ChevronDown, ChevronRight } from "lucide-react";
import { CodeBlock } from "@/components/code/CodeBlock";
import { formatRequestContent } from "./helpers";

function looksLikeJson(text: string) {
  const t = text.trim();
  if (!(t.startsWith("{") || t.startsWith("["))) return false;
  try {
    JSON.parse(t);
    return true;
  } catch {
    return false;
  }
}

/** Plain text, or highlighted JSON when the text is valid JSON. */
export function TextBlock({ text }: { text: string }) {
  if (looksLikeJson(text)) {
    return <CodeBlock code={text} language="json" maxHeight={360} />;
  }
  return <pre className="font-mono text-xs leading-relaxed whitespace-pre-wrap break-words text-foreground">{text}</pre>;
}

/** SQL query block (light highlighting) with a title bar. */
export function SqlPanel({ sql, title = "SQL-query", maxHeight = 360 }: { sql: string; title?: string; maxHeight?: number }) {
  return <CodeBlock code={sql} language="sql" title={title} maxHeight={maxHeight} className="bg-card" />;
}

/** "Verzoek" tab: formatted request, with any ```sql block split out. */
export function RequestContent({ request }: { request: string }) {
  // Format the request content nicely
  const formattedContent = formatRequestContent(request);

  // Check if request contains SQL query
  const sqlMatch = formattedContent.match(/```sql\n([\s\S]*?)\n```/);
  if (sqlMatch) {
    const sql = sqlMatch[1];
    const otherContent = formattedContent.replace(/```sql\n[\s\S]*?\n```/, '').trim();
    return (
      <div className="space-y-3">
        {otherContent && <TextBlock text={otherContent} />}
        <SqlPanel sql={sql} />
      </div>
    );
  }
  return <TextBlock text={formattedContent} />;
}

/** "Resultaat" tab: SQL block first, then the remaining text. */
export function ResultContent({ result }: { result: string }) {
  // Convert result to string if it's not already
  const resultStr = typeof result === 'string' ? result : JSON.stringify(result, null, 2);

  // Check if result contains markdown SQL block
  const sqlMatch = resultStr.match(/```sql\n([\s\S]*?)\n```/);
  if (sqlMatch) {
    const sql = sqlMatch[1];
    // Remove SQL block AND its label from other content
    const otherContent = resultStr
      .replace(/SQL Query:\s*\n```sql\n[\s\S]*?\n```/, '')
      .replace(/```sql\n[\s\S]*?\n```/, '')
      .trim();

    return (
      <div className="space-y-3">
        <SqlPanel sql={sql} />
        {otherContent && <TextBlock text={otherContent.replace(/^Answer:\s*/i, '')} />}
      </div>
    );
  }
  // Regular text
  return <TextBlock text={resultStr} />;
}

/** "State" tab: one collapsible row per state key; raw text when not JSON. */
export function StateContent({ state }: { state: string }) {
  const [open, setOpen] = useState<Set<string>>(new Set());

  const toggle = (key: string) => {
    const next = new Set(open);
    if (next.has(key)) next.delete(key);
    else next.add(key);
    setOpen(next);
  };

  let stateObj: unknown;
  try {
    // Try to format as JSON if possible
    stateObj = JSON.parse(state);
  } catch {
    // If not valid JSON, display as-is
    return <TextBlock text={state} />;
  }
  if (!stateObj || typeof stateObj !== 'object') return <TextBlock text={state} />;

  const entries = Object.entries(stateObj as Record<string, unknown>);
  if (entries.length === 0) return <p className="text-xs text-muted-foreground">Geen state-variabelen.</p>;

  return (
    <div className="space-y-1.5">
      {entries.map(([key, value]) => {
        const isOpen = open.has(key);
        const valueStr = typeof value === 'string' ? value : JSON.stringify(value, null, 2);
        return (
          <div key={key} className="overflow-hidden rounded-md border border-border bg-card">
            <button
              type="button"
              onClick={() => toggle(key)}
              className="flex w-full items-center gap-2 px-3 py-1.5 text-left hover:bg-muted"
              aria-expanded={isOpen}
            >
              {isOpen ? <ChevronDown className="h-3.5 w-3.5 text-muted-foreground" /> : <ChevronRight className="h-3.5 w-3.5 text-muted-foreground" />}
              <span className="font-mono text-xs font-semibold text-foreground">{key}</span>
            </button>
            {isOpen && (
              <div className="border-t border-border bg-sl-grey-50 p-3">
                <TextBlock text={valueStr} />
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
}
